import { supabase } from '@/shared/lib/supabase';

/**
 * adminDashboardService — operational dashboard data for /admin home page.
 *
 * Distinct from adminReportsService (which is analytical / trend-focused).
 * This service answers "what needs my attention right now?"; reports answers
 * "how is the business trending?"
 *
 * Reads from v_admin_dashboard_snapshot — one wide row, refresh on a 60s
 * client-side interval. Per AD-27 (locked arch decision): no caching, no
 * warehouse — read-through view. Promote to a cache table only if p95 >500ms.
 *
 * Spec: _bmad-output/ADMIN_PORTAL_GAP_ANALYSIS_2026-05-13.md §6 A-1.
 */

/** One wide row from v_admin_dashboard_snapshot. */
export interface DashboardSnapshot {
  // Reused from v_admin_dashboard_metrics
  active_customers: number;
  suspended_customers: number;
  churned_customers: number;
  lead_customers: number;
  mrr_zar: number;
  open_tickets: number;
  invoices_in_grace: number;
  invoices_suspended: number;
  failed_provisioning_count: number;
  pending_provisioning_count: number;

  // New operational signals
  unassigned_tickets: number;
  /** Past its due date in SAST. Genuinely late — the number worth a red badge. */
  overdue_invoices_count: number;
  /** Issued and NOT yet due. Money coming, not money chased. Counting these as
   *  overdue is what made paying customers look delinquent (2026-08-30). */
  due_invoices_count: number;
  domains_expiring_30d_count: number;
  revenue_mtd_zar: number;
  reseller_capacity_pct: number | null;
  reseller_max_accounts: number | null;

  // NOTE (A1, 2026-08-03): last_cron_run_at + failing_crons_count were REMOVED
  // from the view. It called admin_cron_health_summary() twice, which
  // seq-scanned cron.job_run_details (274k rows, 132MB, unindexable — the table
  // is owned by supabase_admin) once per job. That was 98.6% of a 12,766ms
  // query against an 8s statement_timeout, i.e. a hard 500 on every /admin load.
  // Removing both columns took the view to 52ms.
  //
  // Cron health is fetched separately by AdminDashboard via adminCronService,
  // and "last run" is derived there as max(last_run_start). Do NOT reintroduce
  // these fields: no staff-facing read path may depend on an unbounded
  // operational log.

  // Snapshot metadata
  snapshot_taken_at: string;
}

/** Month-over-month deltas for the KPI tiles, from v_admin_report_mrr_monthly
 *  (current in-progress month vs the last completed month). */
export interface DashboardTrend {
  mrrDeltaPct: number | null;   // % change vs prior month (null if no prior)
  customersDelta: number | null; // absolute change in active customer count
}

/** One month from v_admin_report_collections_monthly. */
export interface CollectionsMonth {
  month: string;          // 'YYYY-MM-DD', first of the SAST month
  payment_count: number;
  collected_zar: number;
  /** True for the month still in progress — render it as incomplete. */
  is_partial: boolean;
}

/** One product line from v_admin_report_revenue_mix. */
export interface RevenueMixRow {
  target_kind: string;
  line_count: number;
  revenue_zar: number;
}

/** One gateway from v_admin_report_payment_mix. */
export interface PaymentMixRow {
  gateway: string;
  payment_count: number;
  collected_zar: number;
}

/**
 * Re-export, not a second copy. The canonical map lives beside
 * `AdminPaymentRow` in adminBillingService — three maps for one enum is how
 * the payments page came to label `eft_manual` as nothing at all.
 */
export { PAYMENT_GATEWAY_LABELS } from '@/domains/billing/services/adminBillingService';

/**
 * invoice_line.target_kind is a storage value, not a customer-facing word.
 * Mapped here rather than in the view: a label is presentation, and baking it
 * into SQL would make renaming it a migration.
 */
export const REVENUE_MIX_LABELS: Record<string, string> = {
  hosting: 'Hosting',
  service_sku: 'Once-off services',
  service_plan: 'Care plans',
  domain: 'Domains',
  discount: 'Discounts',
  credit: 'Credits',
  manual: 'Manual lines',
};

export const adminDashboardService = {
  async getSnapshot(): Promise<DashboardSnapshot> {
    const { data, error } = await supabase
      .from('v_admin_dashboard_snapshot')
      .select('*')
      .single();
    if (error) {
      throw new Error(`Could not load dashboard snapshot: ${error.message}`);
    }
    return data as DashboardSnapshot;
  },

  /** Fetch the last two monthly rows and compute MoM deltas. Non-fatal:
   *  returns nulls on any error so the dashboard still renders. */
  async getTrend(): Promise<DashboardTrend> {
    try {
      const { data, error } = await supabase
        .from('v_admin_report_mrr_monthly')
        .select('month, mrr_zar, active_customer_count')
        .order('month', { ascending: false })
        .limit(2);
      const rows = (data ?? []) as Array<{ mrr_zar: number; active_customer_count: number }>;
      const cur = rows[0];
      const prev = rows[1];
      if (error || !cur || !prev) return { mrrDeltaPct: null, customersDelta: null };
      const mrrDeltaPct = prev.mrr_zar > 0
        ? Math.round(((cur.mrr_zar - prev.mrr_zar) / prev.mrr_zar) * 100)
        : null;
      return { mrrDeltaPct, customersDelta: cur.active_customer_count - prev.active_customer_count };
    } catch {
      return { mrrDeltaPct: null, customersDelta: null };
    }
  },

  /**
   * Cash received per SAST month, last 12, zero-filled.
   *
   * NOT the same measure as getTrend()'s MRR — that is billed recurring
   * revenue, this is what actually arrived. A customer paying three months late
   * moves this and not that. They are not meant to agree.
   *
   * Throws on error rather than returning []: an empty array here would render
   * as a flat line at zero, which is indistinguishable from a real collapse.
   * The caller's UniversalAsyncBoundary shows the error instead.
   */
  async getCollectionsMonthly(): Promise<CollectionsMonth[]> {
    const { data, error } = await supabase
      .from('v_admin_report_collections_monthly')
      .select('month, payment_count, collected_zar, is_partial')
      .order('month', { ascending: true });
    if (error) throw new Error(`Could not load collections: ${error.message}`);
    return (data ?? []) as CollectionsMonth[];
  },

  /** Revenue by product line across paid invoices. Throws for the same reason. */
  async getRevenueMix(): Promise<RevenueMixRow[]> {
    const { data, error } = await supabase
      .from('v_admin_report_revenue_mix')
      .select('target_kind, line_count, revenue_zar')
      .order('revenue_zar', { ascending: false });
    if (error) throw new Error(`Could not load revenue mix: ${error.message}`);
    return (data ?? []) as RevenueMixRow[];
  },

  /** How customers actually pay — how much of the money still arrives by hand. */
  async getPaymentMix(): Promise<PaymentMixRow[]> {
    const { data, error } = await supabase
      .from('v_admin_report_payment_mix')
      .select('gateway, payment_count, collected_zar')
      .order('payment_count', { ascending: false });
    if (error) throw new Error(`Could not load payment mix: ${error.message}`);
    return (data ?? []) as PaymentMixRow[];
  },
};
